import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SaveIcon from "@mui/icons-material/Save";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery, useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { useState, useEffect, useCallback } from "react";
import { Fragment } from "react/jsx-runtime";
import { Controller, useForm } from "react-hook-form";

import { TaskListRowPropInterface } from "./TaskListRow";

import { listBrands, cancelPreviousOrderRequest, listProject, updateTask } from "@/api";
import { DeleteTask } from "@/components/DeleteTask";
import { CreateTaskInterface } from "@/types/task";
import { calculateTimeDifference } from "@/utils/helpers.utils";

export const TaskListRow: React.FC<TaskListRowPropInterface> = ({
  task,
  refetchTableData,
}) => {
  const { control, reset, getValues, watch } = useForm<CreateTaskInterface>({
    mode: "onBlur",
  });
  const startTimeValue = watch("startTime");
  const endTimeValue = watch("endTime");

  const { id, startTime, endTime, project, tag, description } = task;

  const [edit, setEdit] = useState(false);
  const [query, setQuery] = useState('');
  const toggleEdit = () => setEdit(!edit);

  const memoizedReset = useCallback(() => {
    reset(task);
  }, [reset, task]);

  useEffect(() => {
    memoizedReset();
  }, [memoizedReset]);

  const { isLoading: isLoadingProjectList, data: projects } = useQuery({
    queryKey: ["list-projects"],
    queryFn: async () => await listProject(),
  });

  const { isLoading: isLoadingTagList, data: tags } = useQuery({
    queryKey: ["list-tags", query],
    queryFn: async () => {
      cancelPreviousOrderRequest();
      return await listBrands({
        query,
        limit: 50,
        offset: 0,
      })
    },
  });

  const { mutate: updateTaskMutation, isPending: updateTaskIsPending } =
    useMutation({
      mutationFn: async (data: CreateTaskInterface) => updateTask(id, data),
      onSuccess: () => {
        refetchTableData();
        toggleEdit();
      },
    });

  const submit = () => {
    updateTaskMutation(getValues());
  };

  return (
    <Fragment>
      <TableRow
        key={id}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell component="th" scope="row" sx={{ maxWidth: "20vw" }}>
          {edit ? (
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  placeholder="What are you working on?"
                />
              )}
            />
          ) : (
            description
          )}
        </TableCell>
        <TableCell align="left">
          {edit ? (
            <Controller
              control={control}
              name="tag"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={
                    (tags?.data?.data || [])?.map(({ name }) => name) || []
                  }
                  size="small"
                  onChange={(event, value) => field.onChange(value)}
                  onInputChange={(event, value) => setQuery(value)}
                  sx={{ width: 150 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tag"
                      disabled={isLoadingTagList}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {isLoadingTagList ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          ) : (
            tag
          )}
        </TableCell>
        <TableCell align="left">
          {edit ? (
            <Controller
              control={control}
              name="project"
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={
                    (projects?.data || [])?.map(({ name }) => name) || []
                  }
                  size="small"
                  onChange={(event, value) => field.onChange(value)}
                  sx={{ width: 150 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project"
                      disabled={isLoadingProjectList}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {isLoadingProjectList ? (
                              <CircularProgress size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          ) : (
            project
          )}
        </TableCell>
        <TableCell align="left">
          {edit ? (
            <Controller
              control={control}
              name="startTime"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    onChange={(event) => field.onChange(event?.tz().format())}
                    value={dayjs(startTime)}
                    slotProps={{ textField: { size: "small" } }}
                    disableFuture
                    timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                  />
                </LocalizationProvider>
              )}
            />
          ) : (
            dayjs(startTime).format("YYYY MMM DD HH:mm:ss")
          )}
        </TableCell>
        <TableCell align="left">
          {edit ? (
            <Controller
              control={control}
              name="endTime"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    onChange={(event) => field.onChange(event?.tz().format())}
                    value={dayjs(endTime)}
                    slotProps={{ textField: { size: "small" } }}
                    disableFuture
                    minDate={dayjs(startTimeValue)}
                    timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                  />
                </LocalizationProvider>
              )}
            />
          ) : (
            dayjs(endTime).format("YYYY MMM DD HH:mm:ss")
          )}
        </TableCell>
        <TableCell align="left">
          {calculateTimeDifference(startTimeValue!, endTimeValue!)}
        </TableCell>
        <TableCell align="right">
          <Stack direction="row">
            <IconButton
              color="primary"
              size="small"
              disabled={updateTaskIsPending}
              onClick={toggleEdit}
            >
              {edit ? <CloseIcon /> : <EditIcon />}
            </IconButton>
            {edit && (
              <IconButton
                color="primary"
                size="small"
                disabled={updateTaskIsPending}
                onClick={submit}
              >
                <SaveIcon />
              </IconButton>
            )}
            {!edit && (
              <DeleteTask
                taskId={id}
                refetchTableData={refetchTableData}
              />
            )}
            {!edit && (
              <IconButton
                color="primary"
                size="small"
              >
                <PlayCircleIcon />
              </IconButton>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
